import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Folders,
  ChatCircleDots,
  CurrencyEur,
  UsersThree,
  Plus,
  Clock,
  WarningCircle,
  ArrowsClockwise,
  VideoCamera,
} from '@phosphor-icons/react';
import { api } from '../../lib/api.js';
import { labelize } from '../../lib/labels.js';
import { formatEur, formatEventTime } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.js';
import { Link } from '../../lib/router.js';
import { useDismissable } from '../../lib/use-dismissable.js';

// · #471 · el detalle de la tarea se abre DESDE Inicio, y este módulo es EAGER (App.tsx lo
// importa directo, no con lazy), así que un import normal metería el drawer y su árbol
// —PanelBlock, SectionRail, cronómetro: 6,3 KB gzip medidos— en el chunk de arranque, que
// solo tiene ~16 KB libres de los 140 KB de presupuesto. Con el lazy, el chunk se comparte
// con Proyectos y se pide la primera vez que se pulsa una tarea: entry +0,4 KB.
const TaskDetailDrawer = lazy(() =>
  import('../tareas/TaskDetailDrawer.js').then((m) => ({ default: m.TaskDetailDrawer })),
);

interface TodayEvent {
  id: string;
  title: string;
  start_time: string | null;
  end_time: string | null;
  kind: string;
  description: string | null;
  online_meeting_url: string | null;
  project_id: string | null;
  attendees_count: number;
}

interface TodayTask {
  id: string;
  title: string;
  project_id: string | null;
  project_name: string | null;
  priority: string;
  status: string;
  start_date: string | null;
  due_date: string | null;
}

// #286 · ticket del TRACK pendiente del usuario
interface TodayTrack {
  id: string;
  subject: string;
  project_id: string | null;
  project_name: string | null;
  priority: string;
  status: string;
  target_date: string | null;
  overdue: boolean;
  display_code: string | null;
}

interface Highlight {
  id: string;
  title: string;
  subtitle: string;
  severity: 'info' | 'warning' | 'danger';
  // #383 · enlace directo (recordatorios de seguimiento → ficha del lead)
  href?: string;
}

interface Summary {
  projects_active: number;
  tickets_open: number;
  pending_receivable_cents: number;
  pending_receivable_overdue: number;
  leads_active: number;
  leads_in_proposal: number;
  jornada?: { time: string; label: string; tag: string }[];
  today_events?: TodayEvent[];
  today_tasks?: TodayTask[];
  today_track?: TodayTrack[];
  highlights?: Highlight[];
}

export function DashboardHome(): ReactElement {
  const { user } = useAuth();
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    try {
      const d = await api.dashboardHome();
      setData(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    } finally {
      setRefreshing(false);
    }
  }, []);

  // #272 · el botón de la jornada RE-SINCRONIZA con Outlook (no solo refetch) y luego recarga.
  const refresh = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    try {
      await fetch('/api/v1/team-calendar/sync-outlook', {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {
        /* best-effort: si falla la sync, igual refrescamos los datos */
      });
      const d = await api.dashboardHome();
      setData(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const role = user?.role ?? '';
  // Diseño no ve datos financieros ni de CRM en el dashboard.
  const canSeeFinance = [
    'studio_admin',
    'studio_director',
    'studio_management',
    'developer',
  ].includes(role);
  const canSeeCrm = ['studio_admin', 'studio_director', 'developer'].includes(role);
  const firstName = user?.name?.split(' ')[0] ?? '';
  const now = new Date();
  const greeting = greetingFor(now);
  const dateStr = now.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* R4 responsive · hero envuelve con gap vertical compacto en móvil */}
      <div className="flex items-end justify-between gap-x-6 gap-y-3 mb-6 sm:mb-8 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {greeting},<br />
            {firstName || 'Bienvenido'}.
          </h1>
          <p className="mt-2 opacity-60 text-sm capitalize">
            {dateStr} · {timeStr}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/fichar"
            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[2.75rem] md:min-h-0 rounded-md border border-[var(--color-munya-sand)] text-sm hover:bg-[var(--color-munya-sand)]/30 transition"
            title="Pantalla rápida de fichaje"
          >
            <Clock size={15} weight="regular" />
            Fichar
          </a>
          <button
            type="button"
            onClick={() => {
              const ev = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
              window.dispatchEvent(ev);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[2.75rem] md:min-h-0 rounded-md bg-[var(--color-munya-olive)] text-white text-sm hover:bg-[var(--color-munya-olive-hover)] transition"
            title="Abrir paleta de comandos (⌘K)"
          >
            <Plus size={15} weight="bold" />
            Nuevo
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-md border border-[var(--color-munya-destructive)]/40 bg-[var(--color-munya-destructive)]/10 text-sm">
          No se pudieron cargar los KPIs: {error}
        </div>
      )}

      {/* R6 responsive · KPIs 1 col en móvil, 2 en tablet, 4 en desktop */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10"
        data-focus-hide
      >
        <KpiCard
          icon={Folders}
          label="Proyectos activos"
          value={data?.projects_active ?? '—'}
          hint={data?.projects_active ? 'En curso ahora' : 'Sin proyectos activos'}
          href="/proyectos"
        />
        <KpiCard
          icon={ChatCircleDots}
          label="Tickets abiertos"
          value={data?.tickets_open ?? '—'}
          hint={data?.tickets_open ? 'Track · pendientes + en curso' : 'Sin tickets abiertos'}
          href="/track"
        />
        {canSeeFinance && (
          <KpiCard
            icon={CurrencyEur}
            label="Pendiente cobro"
            value={data ? formatEur(data.pending_receivable_cents) : '—'}
            hint={
              data?.pending_receivable_overdue
                ? `${data.pending_receivable_overdue} facturas vencidas`
                : 'al día'
            }
            warning={!!data?.pending_receivable_overdue}
          />
        )}
        {canSeeCrm && (
          <KpiCard
            icon={UsersThree}
            label="Leads activos"
            value={data?.leads_active ?? '—'}
            hint={
              data?.leads_in_proposal ? `${data.leads_in_proposal} en propuesta` : 'sin propuestas'
            }
            href="/crm"
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <TodayJornadaCard
          events={data?.today_events ?? []}
          tasks={data?.today_tasks ?? []}
          track={data?.today_track ?? []}
          onRefresh={() => void refresh()}
          refreshing={refreshing}
          // · #471 · editar/eliminar la tarea desde el drawer cambia esta misma lista.
          // Recarga sola (`load`), sin la re-sincronización con Outlook de `refresh`.
          onTaskChanged={() => void load()}
        />

        <section className="rounded-lg border border-[var(--color-munya-sand)] bg-[var(--color-munya-surface)]/40 p-4 sm:p-5">
          <h2 className="text-xs uppercase tracking-wider opacity-60 mb-4">
            Pendientes destacados
          </h2>
          {/* #287 · criterios: tickets del TRACK vencidos (danger) + tareas vencidas (warning) +
              facturas vencidas (si rol finanzas). Lo que requiere atención inmediata del usuario. */}
          {(data?.highlights && data.highlights.length > 0) ||
          (canSeeFinance && data && data.pending_receivable_overdue > 0) ? (
            <ul className="space-y-2">
              {canSeeFinance && data && data.pending_receivable_overdue > 0 && (
                <li className="flex items-center justify-between p-3 rounded-md bg-[var(--color-munya-destructive)]/10 border-l-2 border-[var(--color-munya-destructive)]">
                  <div className="flex items-center gap-2">
                    <WarningCircle size={16} className="text-[var(--color-munya-destructive)]" />
                    <span className="text-sm font-medium">
                      {data.pending_receivable_overdue} factura
                      {data.pending_receivable_overdue > 1 ? 's' : ''} vencida
                      {data.pending_receivable_overdue > 1 ? 's' : ''}
                    </span>
                  </div>
                  <Link
                    href="/contabilidad"
                    className="text-xs underline opacity-70 hover:opacity-100"
                  >
                    Ver
                  </Link>
                </li>
              )}
              {(data?.highlights ?? []).map((h) => (
                <li
                  key={h.id}
                  className={`flex items-start gap-2 p-3 rounded-md border-l-2 ${
                    h.severity === 'danger'
                      ? 'bg-[var(--color-munya-destructive)]/10 border-[var(--color-munya-destructive)]'
                      : h.severity === 'warning'
                        ? 'bg-[var(--color-munya-warning)]/10 border-[var(--color-munya-warning)]'
                        : 'bg-[var(--color-munya-sand)]/20 border-[var(--color-munya-olive)]'
                  }`}
                >
                  <WarningCircle
                    size={16}
                    className={
                      h.severity === 'danger'
                        ? 'text-[var(--color-munya-destructive)] mt-0.5 shrink-0'
                        : 'text-[var(--color-munya-warning)] mt-0.5 shrink-0'
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{h.title}</p>
                    <p className="text-[11px] opacity-60">{h.subtitle}</p>
                  </div>
                  {/* #383 · recordatorios de seguimiento (y cualquier highlight con enlace) */}
                  {h.href && (
                    <Link
                      href={h.href}
                      className="shrink-0 text-xs underline opacity-70 hover:opacity-100"
                    >
                      Ver
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState text="Sin avisos críticos. Buen momento para diseñar." />
          )}
        </section>
      </div>
    </>
  );
}

function TodayJornadaCard({
  events,
  tasks,
  track,
  onRefresh,
  refreshing,
  onTaskChanged,
}: {
  events: TodayEvent[];
  tasks: TodayTask[];
  track: TodayTrack[];
  onRefresh: () => void;
  refreshing: boolean;
  onTaskChanged: () => void;
}): ReactElement {
  const [openEvent, setOpenEvent] = useState<TodayEvent | null>(null);
  // · #471 · la tarea se abre AQUÍ, en su drawer. Antes era un enlace: con proyecto te
  // dejaba en la lista de tareas del proyecto (a buscarla otra vez) y sin proyecto te
  // mandaba a /calendario, donde una tarea sin fecha no aparece nunca — quedaba visible
  // en Inicio y sin ninguna forma de abrirla.
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  // Al cerrar, el foco vuelve al botón que abrió el panel; si la tarea ha desaparecido de
  // la lista (completada o eliminada) ese botón ya no está en el documento y no se toca.
  const taskTriggerRef = useRef<HTMLButtonElement | null>(null);
  const closeTask = (): void => {
    setOpenTaskId(null);
    const trigger = taskTriggerRef.current;
    taskTriggerRef.current = null;
    if (trigger?.isConnected) trigger.focus();
  };
  return (
    <section className="rounded-lg border border-[var(--color-munya-sand)] bg-[var(--color-munya-surface)]/40 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs uppercase tracking-wider opacity-60">Tu jornada de hoy</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            title="Re-sincronizar con Outlook y actualizar"
            aria-label="Re-sincronizar con Outlook y actualizar"
            className="opacity-50 hover:opacity-100 disabled:opacity-30 transition inline-flex items-center justify-center p-2 -m-2 md:p-0 md:m-0"
          >
            <ArrowsClockwise size={13} className={refreshing ? 'motion-safe:animate-spin' : ''} />
          </button>
          <Link
            href="/calendario"
            className="text-[10px] underline opacity-60 hover:opacity-100 inline-flex items-center py-2 -my-2 md:py-0 md:my-0"
          >
            Ver semana
          </Link>
        </div>
      </div>

      {/* A12 · Eventos del calendario MS365 */}
      <h3 className="text-[10px] uppercase tracking-wide opacity-50 mb-1.5">
        Eventos del calendario ({events.length})
      </h3>
      {events.length === 0 ? (
        <p className="text-xs italic opacity-50 py-2">Sin eventos hoy. Agenda despejada.</p>
      ) : (
        <ul className="space-y-1.5 mb-4">
          {events.map((e) => (
            <li key={e.id}>
              <button
                onClick={() => setOpenEvent(e)}
                className="w-full min-w-0 flex items-center justify-between gap-2 p-2.5 rounded-md bg-[var(--color-munya-surface)]/60 border border-[var(--color-munya-sand)]/60 hover:border-[var(--color-munya-olive)]/60 hover:bg-[var(--color-munya-sand)]/30 transition text-left"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="font-mono text-xs opacity-70 w-16 shrink-0">
                    {e.start_time ? formatEventTime(e.start_time) : '—'}
                  </span>
                  <span className="text-sm truncate min-w-0 flex-1">{e.title}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* #390 · emoji decorativo → icono Phosphor · kind legible vía labelize */}
                  {e.online_meeting_url && (
                    <VideoCamera
                      size={13}
                      weight="duotone"
                      className="text-[var(--color-munya-olive)]"
                    />
                  )}
                  <span className="text-[10px] uppercase opacity-50 tracking-wide">
                    {labelize(e.kind)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* A12 · Tareas del día asignadas */}
      <h3 className="text-[10px] uppercase tracking-wide opacity-50 mb-1.5">
        Tareas del día ({tasks.length})
      </h3>
      {tasks.length === 0 ? (
        <p className="text-xs italic opacity-50 py-2">
          Sin tareas asignadas hoy. Espacio para lo importante.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {tasks.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={(e) => {
                  taskTriggerRef.current = e.currentTarget;
                  setOpenTaskId(t.id);
                }}
                // Mismo aspecto que la fila de evento de arriba · área táctil de 44px en móvil
                className="w-full min-w-0 flex items-center justify-between gap-2 p-2.5 min-h-[2.75rem] md:min-h-0 rounded-md bg-[var(--color-munya-surface)]/60 border border-[var(--color-munya-sand)]/60 hover:border-[var(--color-munya-olive)]/60 hover:bg-[var(--color-munya-sand)]/30 transition text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      t.priority === 'urgent'
                        ? 'bg-[var(--color-munya-destructive)]'
                        : t.priority === 'high'
                          ? 'bg-[var(--color-munya-warning)]'
                          : 'bg-[var(--color-munya-olive)]/50'
                    }`}
                  />
                  <span className="text-sm truncate flex-1">{t.title}</span>
                </div>
                <span className="text-[10px] opacity-60 truncate max-w-[120px]">
                  {t.project_name ?? 'Tarea interna'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* #286 · Track: tickets pendientes del usuario, vencidos primero */}
      <h3 className="text-[10px] uppercase tracking-wide opacity-50 mb-1.5 mt-4">
        Track ({track.length})
      </h3>
      {track.length === 0 ? (
        <p className="text-xs italic opacity-50 py-2">
          Sin tickets del track asignados. Bandeja limpia.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {track.slice(0, 10).map((t) => (
            <li key={t.id}>
              {/* · #471 · mismo defecto que las tareas, un bloque más abajo: el ticket
                  aterrizaba en el resumen del proyecto (a buscarlo en la pestaña Track) y,
                  sin proyecto, en /calendario. El enlace profundo `?id=` ya lo lee TrackTab
                  desde #453, así que abre el ticket; sin proyecto, la bandeja global. */}
              <Link
                href={t.project_id ? `/proyectos/${t.project_id}/track?id=${t.id}` : '/track'}
                className={`flex items-center justify-between p-2.5 rounded-md border transition hover:border-[var(--color-munya-olive)]/60 ${
                  t.overdue
                    ? 'bg-[var(--color-munya-destructive)]/10 border-[var(--color-munya-destructive)]/40'
                    : 'bg-[var(--color-munya-surface)]/60 border-[var(--color-munya-sand)]/60'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {t.overdue && (
                    <WarningCircle
                      size={13}
                      className="text-[var(--color-munya-destructive)] shrink-0"
                    />
                  )}
                  <span className="text-sm truncate flex-1">
                    {t.display_code ? `${t.display_code} · ` : ''}
                    {t.subject}
                  </span>
                </div>
                <span className="text-[10px] opacity-60 shrink-0 ml-2">
                  {t.target_date ? (t.overdue ? `venció ${t.target_date}` : t.target_date) : '—'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {openEvent && <EventDetailModal event={openEvent} onClose={() => setOpenEvent(null)} />}

      {/* · #471 · el detalle vive en el mismo drawer que usa Proyectos · sin salir de Inicio.
          No se cierra al guardar: la lista de detrás se refresca y el panel sigue abierto
          (arrancar el cronómetro y seguir es el caso normal). Borrar sí lo cierra. */}
      {openTaskId && (
        <Suspense fallback={null}>
          <TaskDetailDrawer taskId={openTaskId} onClose={closeTask} onUpdated={onTaskChanged} />
        </Suspense>
      )}
    </section>
  );
}

function EventDetailModal({
  event,
  onClose,
}: {
  event: TodayEvent;
  onClose: () => void;
}): ReactElement {
  // · #456 · el fondo solo cierra con el gesto completo (mousedown + mouseup sobre el fondo)
  const { backdropProps } = useDismissable({
    onDismiss: onClose,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      {...backdropProps}
    >
      {/* R7 responsive · modal full-width en móvil, alto acotado con scroll interno */}
      <div
        className="bg-[var(--color-munya-cream)] rounded-lg w-full max-w-none sm:max-w-md p-4 sm:p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3">
          {/* #390 · kind legible vía labelize (nada de enums crudos) */}
          <p className="text-[10px] uppercase tracking-wider opacity-50">{labelize(event.kind)}</p>
          <h3 className="font-semibold text-base mt-0.5">{event.title}</h3>
        </header>
        <p className="text-sm opacity-70 mb-3">
          {event.start_time ? formatEventTime(event.start_time) : '—'}
          {event.end_time ? ` – ${formatEventTime(event.end_time)}` : ''}
          {event.attendees_count > 0 && ` · ${event.attendees_count} participantes`}
        </p>
        {event.description && (
          <p className="text-sm whitespace-pre-wrap break-words mb-3 opacity-80 max-h-[40vh] overflow-y-auto">
            {event.description}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {event.online_meeting_url && (
            <a
              href={event.online_meeting_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 min-h-[2.75rem] md:min-h-0 text-xs rounded-md bg-[var(--color-munya-olive)] text-white inline-flex items-center gap-1.5"
            >
              {/* #390 · emoji decorativo → icono Phosphor */}
              <VideoCamera size={14} weight="duotone" /> Unirse a la videollamada
            </a>
          )}
          {event.project_id && (
            <Link
              href={`/proyectos/${event.project_id}`}
              className="px-3 py-1.5 min-h-[2.75rem] md:min-h-0 text-xs rounded-md border border-[var(--color-munya-sand)] hover:bg-[var(--color-munya-sand)]/30 inline-flex items-center"
            >
              Ver proyecto
            </Link>
          )}
          <button
            onClick={onClose}
            className="px-3 py-1.5 min-h-[2.75rem] md:min-h-0 text-xs rounded-md border border-[var(--color-munya-sand)] hover:bg-[var(--color-munya-sand)]/30 ml-auto inline-flex items-center"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function greetingFor(d: Date): string {
  const h = d.getHours();
  if (h < 6) return 'Buenas noches';
  if (h < 13) return 'Buenos días';
  if (h < 21) return 'Buenas tardes';
  return 'Buenas noches';
}

interface KpiProps {
  icon: typeof Folders;
  label: string;
  value: string | number;
  hint?: string;
  disabled?: boolean;
  warning?: boolean;
  href?: string;
}

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  disabled,
  warning,
  href,
}: KpiProps): ReactElement {
  const inner = (
    <>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-wider opacity-60">{label}</span>
        <Icon
          size={16}
          weight="regular"
          className={warning ? 'text-[var(--color-munya-destructive)]' : 'opacity-50'}
        />
      </div>
      {/* #390 §5.7 · cifra tabular para que los KPIs alineen dígitos */}
      <p
        className={`text-3xl font-bold leading-none tabular-nums ${warning ? 'text-[var(--color-munya-destructive)]' : ''}`}
      >
        {value}
      </p>
      {hint && <p className="text-xs opacity-50 mt-2">{hint}</p>}
    </>
  );
  // #390 §5.7 · padding unificado con PipelineKpis (p-3 = 24px)
  const base = 'block p-3 rounded-lg border bg-[var(--color-munya-surface)]/60 transition';
  if (disabled) {
    return (
      <div
        className={`${base} border-[var(--color-munya-sand)]/60 opacity-50 cursor-not-allowed`}
        title={hint}
      >
        {inner}
      </div>
    );
  }
  if (href) {
    return (
      <Link
        href={href}
        // #390 §5.11 · tarjeta clicable: sombra suave al hover, transición corta
        className={`${base} border-[var(--color-munya-sand)] hover:border-[var(--color-munya-olive)] hover:bg-[var(--color-munya-surface)]/80 cursor-pointer hover:shadow-[var(--shadow-soft)] duration-150`}
      >
        {inner}
      </Link>
    );
  }
  return <div className={`${base} border-[var(--color-munya-sand)]`}>{inner}</div>;
}

function EmptyState({ text }: { text: string }): ReactElement {
  return <p className="text-sm opacity-50 italic text-center py-8">{text}</p>;
}
